import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useToast } from '../../contexts/ToastContext'
import * as api from '../../lib/api'

/**
 * IvideonSettings — настройки интеграции Ivideon.
 * 
 * Основной способ: OAuth2 — «Подключить» → перенаправление на Ivideon → логин → Разрешить → готово.
 * Fallback: API ключ (xtok-...) — если OAuth не настроен на сервере.
 */
export default function IvideonSettings({ oauthCode, oauthState }) {
  const { profile } = useAuth()
  const toast = useToast()

  // Конфигурация
  const [hasKey, setHasKey] = useState(false)
  const [config, setConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [oauthAvailable, setOauthAvailable] = useState(false)

  // Ввод API ключа (fallback)
  const [showApiKeyInput, setShowApiKeyInput] = useState(false)
  const [apiKeyInput, setApiKeyInput] = useState('')
  const [testing, setTesting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testResult, setTestResult] = useState(null)

  // Камеры из Ivideon
  const [ivideonCameras, setIvideonCameras] = useState([])
  const [camerasLoading, setCamerasLoading] = useState(false)

  // Камеры уже в БД
  const [savedCameras, setSavedCameras] = useState([])
  const [selectedKeys, setSelectedKeys] = useState(new Set())
  const [syncing, setSyncing] = useState(false)

  // Отключение
  const [disconnecting, setDisconnecting] = useState(false)
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false)

  // OAuth loading
  const [oauthLoading, setOauthLoading] = useState(false)

  useEffect(() => {
    loadConfig()
    checkOAuth()
  }, [])

  // Обработка OAuth callback (когда вернулись с Ivideon)
  useEffect(() => {
    if (oauthCode && oauthState) {
      handleOAuthCallback(oauthCode, oauthState)
    }
  }, [oauthCode, oauthState])

  const checkOAuth = async () => {
    try {
      const result = await api.checkIvideonOAuth()
      setOauthAvailable(result.oauth_available)
    } catch (err) {
      // не критично
    }
  }

  const loadConfig = async () => {
    setLoading(true)
    try {
      const result = await api.getIvideonConfig()
      setHasKey(result.has_key)
      setConfig(result.config)
      if (result.has_key) {
        await loadCameras()
      }
    } catch (err) {
      console.error('Ошибка загрузки конфигурации Ivideon:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadCameras = async () => {
    setCamerasLoading(true)
    try {
      const result = await api.listIvideonCameras()
      setIvideonCameras(result.cameras || [])

      const activePvzId = api.getActivePvzId()
      const dbCameras = activePvzId ? await api.getAllCamerasForAdmin(activePvzId) : []
      const ivideonDbCameras = dbCameras.filter(c => c.ivideon_server_id)
      setSavedCameras(ivideonDbCameras)

      const keys = new Set()
      ivideonDbCameras.forEach(c => {
        keys.add(`${c.ivideon_server_id}:${c.ivideon_camera_id}`)
      })
      setSelectedKeys(keys)
    } catch (err) {
      if (err.code === 'INVALID_KEY') {
        toast.error('Токен Ivideon недействителен. Подключитесь заново.')
      } else {
        console.error('Ошибка загрузки камер:', err)
      }
    } finally {
      setCamerasLoading(false)
    }
  }

  // ── OAuth: Начать авторизацию ──────────────────────────────
  const handleOAuthStart = async () => {
    setOauthLoading(true)
    try {
      const result = await api.startIvideonOAuth()
      if (result.oauth_available && result.auth_url) {
        // Перенаправляем на Ivideon
        window.location.href = result.auth_url
      } else {
        // OAuth не доступен — показываем ввод API ключа
        setShowApiKeyInput(true)
        toast.info('OAuth не настроен. Используйте API ключ.')
      }
    } catch (err) {
      toast.error('Ошибка: ' + err.message)
      setShowApiKeyInput(true)
    } finally {
      setOauthLoading(false)
    }
  }

  // ── OAuth: Callback после возврата с Ivideon ───────────────
  const handleOAuthCallback = async (code, state) => {
    setLoading(true)
    try {
      const result = await api.completeIvideonOAuth(code, state)
      if (result.success) {
        toast.success(`Ivideon подключён! Камер: ${result.cameras_count}`)
        await loadConfig()
      } else {
        toast.error('Не удалось подключить Ivideon')
      }
    } catch (err) {
      toast.error('Ошибка OAuth: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  // ── API Key: Сохранить ────────────────────────────────────
  const handleSaveKey = async () => {
    if (!apiKeyInput.trim()) return
    setSaving(true)
    try {
      await api.saveIvideonKey(apiKeyInput.trim())
      toast.success('API ключ Ivideon сохранён!')
      setApiKeyInput('')
      setTestResult(null)
      setShowApiKeyInput(false)
      await loadConfig()
    } catch (err) {
      if (err.code === 'INVALID_KEY') {
        toast.error('Неверный API ключ')
      } else {
        toast.error('Ошибка сохранения: ' + err.message)
      }
    } finally {
      setSaving(false)
    }
  }

  // ── API Key: Проверить ────────────────────────────────────
  const handleTestKey = async () => {
    if (!apiKeyInput.trim()) return
    setTesting(true)
    setTestResult(null)
    try {
      const result = await api.testIvideonKey(apiKeyInput.trim())
      setTestResult(result)
      toast.success(`Ключ подтверждён! Камер: ${result.cameras_count}`)
    } catch (err) {
      setTestResult({ success: false, error: err.message })
      toast.error('Ошибка: ' + err.message)
    } finally {
      setTesting(false)
    }
  }

  // Переключение камеры
  const toggleCamera = (cam) => {
    const key = `${cam.ivideon_server_id}:${cam.ivideon_camera_id}`
    setSelectedKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) { next.delete(key) } else { next.add(key) }
      return next
    })
  }

  // Синхронизировать выбранные камеры
  const handleSyncCameras = async () => {
    if (!profile?.org_id || !profile?.pvz_id) return
    setSyncing(true)
    try {
      const selectedCameras = ivideonCameras.filter(c =>
        selectedKeys.has(`${c.ivideon_server_id}:${c.ivideon_camera_id}`)
      )
      await api.syncIvideonCameras(profile.org_id, profile.pvz_id, selectedCameras)
      toast.success(`Сохранено ${selectedCameras.length} камер!`)
      await loadCameras()
    } catch (err) {
      toast.error('Ошибка синхронизации: ' + err.message)
    } finally {
      setSyncing(false)
    }
  }

  // Отключить интеграцию
  const handleDisconnect = async () => {
    setDisconnecting(true)
    try {
      await api.disconnectIvideon()
      toast.success('Интеграция с Ivideon отключена')
      setHasKey(false)
      setConfig(null)
      setIvideonCameras([])
      setSavedCameras([])
      setSelectedKeys(new Set())
      setShowDisconnectConfirm(false)
    } catch (err) {
      toast.error('Ошибка отключения: ' + err.message)
    } finally {
      setDisconnecting(false)
    }
  }

  const hasChanges = (() => {
    const savedKeys = new Set()
    savedCameras.forEach(c => savedKeys.add(`${c.ivideon_server_id}:${c.ivideon_camera_id}`))
    if (selectedKeys.size !== savedKeys.size) return true
    for (const key of selectedKeys) {
      if (!savedKeys.has(key)) return true
    }
    return false
  })()

  if (loading) {
    return (
      <div className="settings-section animate-fade-in">
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }} />
          Загрузка настроек Ivideon...
        </div>
      </div>
    )
  }

  return (
    <div className="settings-section animate-fade-in">
      {/* Заголовок */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        marginBottom: '24px', padding: '16px 20px',
        background: 'linear-gradient(135deg, var(--surface-hover) 0%, var(--bg-secondary) 100%)',
        borderRadius: 'var(--border-radius-lg)',
        border: '1px solid var(--surface-border)'
      }}>
        <span style={{ fontSize: '32px', display: 'inline-flex' }}><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--wb-primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg></span>
        <div>
          <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--text-primary)' }}>
            Интеграция с Ivideon
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
            Подключите камеры видеонаблюдения — стрим HLS со звуком, архив, экспорт MP4
          </p>
        </div>
        {hasKey && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '6px 14px', borderRadius: '20px',
              background: 'rgba(16, 185, 129, 0.1)', color: '#10B981',
              fontSize: '13px', fontWeight: 600
            }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10B981' }} />
              Подключено
            </span>
          </div>
        )}
      </div>

      {/* ═══ НЕ ПОДКЛЮЧЕНО ═══ */}
      {!hasKey && (
        <div style={{
          padding: '32px 24px',
          background: 'var(--bg-secondary)',
          borderRadius: 'var(--border-radius-lg)',
          border: '1px solid var(--surface-border)',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px', display: 'flex', justifyContent: 'center' }}><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg></div>
          <h4 style={{ margin: '0 0 8px', color: 'var(--text-primary)', fontSize: '18px' }}>
            Подключите камеры Ivideon
          </h4>
          <p style={{ margin: '0 0 24px', fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.5, maxWidth: '440px', marginLeft: 'auto', marginRight: 'auto' }}>
            Нажмите кнопку ниже — вы будете перенаправлены на страницу Ivideon для авторизации. 
            После входа нажмите «Разрешить» и камеры подключатся автоматически.
          </p>

          {/* Основная кнопка — OAuth */}
          <button
            className="btn btn-primary"
            onClick={handleOAuthStart}
            disabled={oauthLoading}
            style={{
              padding: '12px 32px',
              fontSize: '15px',
              fontWeight: 600,
              borderRadius: '10px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {oauthLoading ? (
              <><span className="btn-spinner-inline" /> Подключение...</>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                  <polyline points="10 17 15 12 10 7"/>
                  <line x1="15" y1="12" x2="3" y2="12"/>
                </svg>
                Подключить Ivideon
              </>
            )}
          </button>

          {/* Fallback — API ключ */}
          {!showApiKeyInput && (
            <div style={{ marginTop: '24px' }}>
              <button
                onClick={() => setShowApiKeyInput(true)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: '13px', color: 'var(--text-tertiary)',
                  textDecoration: 'underline', textUnderlineOffset: '3px'
                }}
              >
                Или подключить через API ключ
              </button>
            </div>
          )}

          {/* Форма API ключа */}
          {showApiKeyInput && (
            <div style={{
              marginTop: '24px', padding: '20px',
              background: 'var(--surface-hover)',
              borderRadius: 'var(--border-radius-md)',
              border: '1px solid var(--surface-border)',
              textAlign: 'left',
            }}>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px', lineHeight: 1.5 }}>
                <strong>Инструкция:</strong> Зайдите в <a href="https://my.ivideon.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>my.ivideon.com</a> → 
                Профиль → Ключи доступа к API → Создать ключ → Скопируйте ключ (<code style={{ fontSize: '11px', background: 'rgba(255,255,255,0.05)', padding: '1px 4px', borderRadius: '3px' }}>xtok-...</code>)
              </div>

              <div className="input-group">
                <input
                  className="input"
                  type="text"
                  placeholder="xtok-100K37-..."
                  value={apiKeyInput}
                  onChange={e => setApiKeyInput(e.target.value)}
                  style={{ fontSize: '13px', fontFamily: 'monospace' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button className="btn btn-secondary btn-sm" onClick={handleTestKey} disabled={!apiKeyInput.trim() || testing}>
                  {testing ? <><span className="btn-spinner-inline" /></> : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'middle'}}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg> Проверить</>}
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleSaveKey} disabled={!apiKeyInput.trim() || saving}>
                  {saving ? <><span className="btn-spinner-inline" /></> : 'Сохранить'}
                </button>
                <button className="btn btn-sm" onClick={() => { setShowApiKeyInput(false); setTestResult(null) }}
                  style={{ marginLeft: 'auto', background: 'none', color: 'var(--text-tertiary)' }}>
                  Отмена
                </button>
              </div>

              {testResult && (
                <div style={{
                  marginTop: '12px', padding: '10px 14px',
                  borderRadius: '8px',
                  background: testResult.success ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
                  border: `1px solid ${testResult.success ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`,
                  color: testResult.success ? '#10B981' : '#EF4444',
                  fontSize: '13px', fontWeight: 500
                }}>
                  {testResult.success ? `Найдено камер: ${testResult.cameras_count}` : testResult.error}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══ ПОДКЛЮЧЕНО ═══ */}
      {hasKey && (
        <>
          {/* Информация о подключении */}
          <div style={{
            padding: '16px 20px',
            background: 'var(--bg-secondary)',
            borderRadius: 'var(--border-radius-lg)',
            border: '1px solid var(--surface-border)',
            marginBottom: '20px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                Последняя синхронизация: {' '}
                <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                  {config?.last_synced_at
                    ? new Date(config.last_synced_at).toLocaleString('ru-RU')
                    : 'Ещё не синхронизировали'}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-secondary btn-sm" onClick={loadCameras} disabled={camerasLoading}>
                {camerasLoading ? <span className="btn-spinner-inline" /> : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>} Обновить
              </button>
              <button
                className="btn btn-sm"
                onClick={() => setShowDisconnectConfirm(true)}
                style={{ background: 'rgba(239,68,68,0.08)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.2)' }}
              >
                Отключить
              </button>
            </div>
          </div>

          {/* Список камер */}
          {camerasLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
              <div className="spinner" style={{ margin: '0 auto 16px' }} />
              Загрузка камер из Ivideon...
            </div>
          ) : ivideonCameras.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '40px',
              background: 'var(--bg-secondary)',
              borderRadius: 'var(--border-radius-lg)',
              border: '1px solid var(--surface-border)',
              color: 'var(--text-secondary)', fontSize: '14px'
            }}>
              На этом аккаунте Ivideon камер не найдено
            </div>
          ) : (
            <>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: '16px'
              }}>
                <h4 style={{ margin: 0, fontSize: '15px', color: 'var(--text-primary)' }}>
                  Камеры Ivideon ({ivideonCameras.length})
                </h4>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  Выбрано: <strong style={{ color: 'var(--text-primary)' }}>{selectedKeys.size}</strong>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {ivideonCameras.map(cam => {
                  const key = `${cam.ivideon_server_id}:${cam.ivideon_camera_id}`
                  const isSelected = selectedKeys.has(key)
                  return (
                    <label
                      key={key}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '12px',
                        padding: '12px 16px',
                        background: isSelected ? 'rgba(124, 58, 237, 0.04)' : 'var(--bg-secondary)',
                        border: `1px solid ${isSelected ? 'rgba(124, 58, 237, 0.2)' : 'var(--surface-border)'}`,
                        borderRadius: 'var(--border-radius-md)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <input type="checkbox" checked={isSelected} onChange={() => toggleCamera(cam)} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                        }}>
                          {cam.name}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px', display: 'flex', gap: '8px' }}>
                          {cam.sound_enabled && <span><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'middle',marginRight:'2px'}}><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg> Звук</span>}
                          {cam.archive_days > 0 && <span><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'middle',marginRight:'2px'}}><rect x="2" y="7" width="20" height="15" rx="2" ry="2"/><polyline points="17 2 12 7 7 2"/></svg> Архив {cam.archive_days} дн.</span>}
                          {cam.device_model && <span>{cam.vendor} {cam.device_model}</span>}
                        </div>
                      </div>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        fontSize: '11px', fontWeight: 600,
                        color: cam.online ? '#10B981' : '#EF4444'
                      }}>
                        <span style={{
                          width: '6px', height: '6px', borderRadius: '50%',
                          background: cam.online ? '#10B981' : '#EF4444'
                        }} />
                        {cam.online ? 'Online' : 'Offline'}
                      </span>
                    </label>
                  )
                })}
              </div>

              <div style={{
                position: 'sticky', bottom: '0',
                padding: '16px 0',
                background: 'var(--bg-primary)',
                borderTop: '1px solid var(--surface-border)',
                marginTop: '8px',
                display: 'flex', justifyContent: 'flex-end', gap: '12px',
              }}>
                <button
                  className="btn btn-primary"
                  onClick={handleSyncCameras}
                  disabled={syncing || !hasChanges}
                >
                  {syncing ? <><span className="btn-spinner-inline" /> Сохранение...</> : hasChanges ? `Сохранить выбор (${selectedKeys.size})` : 'Всё сохранено'}
                </button>
              </div>
            </>
          )}
        </>
      )}

      {/* Модалка подтверждения отключения */}
      {showDisconnectConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setShowDisconnectConfirm(false)}>
          <div style={{
            background: 'var(--bg-primary)',
            borderRadius: 'var(--border-radius-lg)',
            padding: '24px', maxWidth: '400px', width: '90%',
            border: '1px solid var(--surface-border)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', fontSize: '16px', color: 'var(--text-primary)' }}>
              Отключить Ivideon?
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              Доступ будет отозван. Камеры останутся в списке, 
              но потоковое видео станет недоступным. Вы сможете подключиться повторно.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowDisconnectConfirm(false)}>
                Отмена
              </button>
              <button
                className="btn"
                onClick={handleDisconnect}
                disabled={disconnecting}
                style={{ background: '#EF4444', color: '#fff', border: 'none' }}
              >
                {disconnecting ? <><span className="btn-spinner-inline" /> Отключение...</> : 'Отключить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
